import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import AuthImage from "@/components/AuthImage";
import { Loader2, Sparkles, ArrowUpRight, Crown, Scissors } from "lucide-react";
import { toast } from "sonner";

export default function OccasionResult() {
  const { id } = useParams();
  const { user } = useAuth();
  const [a, setA] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tryOnLoadingId, setTryOnLoadingId] = useState(null);
  const [tryOns, setTryOns] = useState({});

  useEffect(() => {
    api.get(`/occasion-analyses/${id}`)
      .then((r) => setA(r.data))
      .catch(() => toast.error("Could not load reading"))
      .finally(() => setLoading(false));
  }, [id]);

  const generateTryOn = async (lookId) => {
    if (!user?.is_premium) {
      toast.error("Virtual try-on is a Premium feature.");
      return;
    }
    setTryOnLoadingId(lookId);
    try {
      const r = await api.post("/occasion/tryon", { analysis_id: id, look_id: lookId });
      setTryOns((p) => ({ ...p, [lookId]: r.data.result_path }));
      toast.success("Try-on ready.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Try-on failed");
    } finally {
      setTryOnLoadingId(null);
    }
  };

  if (loading) {
    return (
      <main className="crown-container py-20 text-center" data-testid="occ-result-loading">
        <Loader2 className="animate-spin text-gold mx-auto" strokeWidth={1.5} />
        <p className="font-overline mt-4">Reading…</p>
      </main>
    );
  }
  if (!a) return <main className="crown-container py-20">Not found.</main>;

  const r = a.result || {};
  const palette = r.color_palette || [];

  return (
    <main className="crown-container py-12 md:py-16" data-testid="occasion-result-page">
      {/* Header */}
      <p className="font-overline mb-3">{a.occasion_meta?.subtitle}</p>
      <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-3">{a.occasion_name}.</h1>
      <p className="text-ash max-w-2xl mb-12">{r.styling_notes}</p>

      {/* Profile facets */}
      <section className="grid grid-cols-2 md:grid-cols-4 gap-px bg-bone/10 mb-12" data-testid="occ-facets">
        {[
          ["Face shape", r.face_shape],
          ["Skin tone", r.skin_tone],
          ["Undertone", r.undertone],
          ["Body type", r.body_type],
        ].map(([k, v]) => (
          <div key={k} className="bg-espresso p-6">
            <p className="font-overline text-ash">{k}</p>
            <p className="font-serif text-2xl mt-2 capitalize text-gold">{v || "—"}</p>
          </div>
        ))}
      </section>

      {/* Palette */}
      {palette.length > 0 && (
        <section className="mb-16" data-testid="occ-palette">
          <p className="font-overline mb-4">Your ceremony palette</p>
          <div className="flex flex-wrap gap-3">
            {palette.map((hex, i) => {
              const clean = String(hex).replace("#", "");
              return (
                <div key={i} className="flex items-center gap-3 border border-bone/15 px-4 py-3">
                  <div className="w-8 h-8" style={{ backgroundColor: `#${clean}` }} />
                  <p className="font-mono text-xs">#{clean.toUpperCase()}</p>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Looks */}
      <section data-testid="occ-recommendations">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <p className="font-overline flex items-center gap-2"><Sparkles size={12} strokeWidth={1.5} /> Curated looks</p>
            <h2 className="font-serif text-3xl mt-2">The looks made for you.</h2>
          </div>
          {!user?.is_premium && (
            <Link to="/pricing" className="font-overline text-gold flex items-center gap-1" data-testid="occ-upgrade-cta">
              <Crown size={14} strokeWidth={1.5} /> Unlock virtual try-on
            </Link>
          )}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {a.recommended_looks.map((l) => (
            <article key={l.id} className="crown-card group" data-testid={`occ-look-${l.id}`}>
              <div className="aspect-[16/10] overflow-hidden relative">
                {tryOns[l.id] ? (
                  <AuthImage path={tryOns[l.id]} alt={`${l.name} try-on`} className="w-full h-full object-cover" testId={`occ-tryon-img-${l.id}`} />
                ) : (
                  <img src={l.image_url} alt={l.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                )}
                <div className="absolute top-3 left-3 font-overline text-[0.65rem] bg-espresso/80 backdrop-blur px-2 py-1 text-gold">
                  {l.category.replace("-", " ")}
                </div>
                {tryOns[l.id] && (
                  <div className="absolute top-3 right-3 font-overline text-[0.65rem] bg-gold text-espresso px-2 py-1">
                    Try-on
                  </div>
                )}
              </div>
              <div className="p-6 space-y-4">
                <div>
                  <h3 className="font-serif text-2xl">{l.name}</h3>
                  <p className="text-ash text-sm leading-relaxed mt-2 line-clamp-2">{l.description}</p>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-start gap-2">
                    <Scissors size={12} className="text-gold mt-1" strokeWidth={1.5} />
                    <p><span className="font-overline text-ash text-[0.6rem] mr-2">Hair</span>{l.hair_pairing}</p>
                  </div>
                  <p className="text-bone"><span className="font-overline text-ash text-[0.6rem] mr-2">Accessories</span>{l.accessories}</p>
                  <p className="text-bone"><span className="font-overline text-ash text-[0.6rem] mr-2">Stylist</span>{l.stylist_notes}</p>
                </div>
                <div className="pt-2">
                  <button
                    onClick={() => generateTryOn(l.id)}
                    disabled={tryOnLoadingId === l.id || !user?.is_premium}
                    className="crown-btn-primary text-[0.7rem] py-2 px-4 disabled:opacity-40"
                    data-testid={`occ-tryon-btn-${l.id}`}
                  >
                    {tryOnLoadingId === l.id ? (
                      <><Loader2 className="animate-spin" size={12} strokeWidth={1.5} /> Composing</>
                    ) : (
                      <>Virtual try-on <ArrowUpRight size={12} strokeWidth={1.5} /></>
                    )}
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </main>
  );
}
